import { memo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./SignupMarketing.scss";
import { signupPageAuthShape, resolutionPropType } from "app/utils/propTypes";
import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import ChangePasswordContainer from "app/pages/Auth/ChangePassword/ChangePasswordContainer";
import ResetPasswordContainer from "app/pages/Auth/ResetPassword/ResetPasswordContainer";
import SignupPageBackground from "app/pages/SignupPage/Auth/SignupMarketing/SignupPageBackground";
import { AuthTabs } from "app/features/auth/components/authTabs/AuthTabs";
import { useDeviceType } from "app/hooks";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { useIntl } from "react-intl";

const BACKGROUND_MOBILE_HEIGHT = {
	SMALL: "small",
	LARGE: "large",
};

const AuthForm = ({ onAuthSuccess, defaultPage }) => {
	return (
		<Routes>
			<Route path="/changepassword" element={<ChangePasswordContainer />} />
			<Route path="/resetpassword" element={<ResetPasswordContainer />} />
			<Route
				path="/login"
				element={
					<AuthTabs
						navigateOnChange
						onAuthSuccess={onAuthSuccess}
						className="signup-marketing__auth-tabs"
						defaultValue="login"
					/>
				}
			/>
			<Route
				path="/signup"
				element={
					<AuthTabs
						navigateOnChange
						onAuthSuccess={onAuthSuccess}
						className="signup-marketing__auth-tabs"
						defaultValue="signup"
					/>
				}
			/>

			<Route path="*" element={<Navigate to={defaultPage} replace />} />
		</Routes>
	);
};

AuthForm.propTypes = {
	onAuthSuccess: PropTypes.func,
	defaultPage: PropTypes.oneOf(["login", "signup"]),
};

const SignupMarketing = ({ resolution, signupPage = {} }) => {
	const navigate = useNavigate();
	const { isDesktop } = useDeviceType();
	const location = useLocation();
	const { formatMessage } = useIntl();

	const photoClass = classNames({
		"signup-marketing__photo": true,
		"signup-marketing__photo--small":
			signupPage.photoSmallBackgroundHeight === BACKGROUND_MOBILE_HEIGHT.SMALL,
	});

	const defaultPage = "signup";

	const getHeroSubtitle = () => {
		const sanityHeroSubtitle = signupPage?.heroTexts?.subtitle;
		const isSignupPage = location.pathname.includes("/signup");

		if (sanityHeroSubtitle) {
			return sanityHeroSubtitle;
		}

		if (isSignupPage) {
			return signupPage?.signupFormHeadline || formatMessage({ id: "auth.signup.headline" });
		}

		return formatMessage({ id: "auth.signin.headline" });
	};

	const heroSubtitle = getHeroSubtitle();

	const redirectOnAuthSuccess = useCallback(() => {
		const redirectUrl = signupPage.redirectUrl;
		if (redirectUrl) {
			window.location = redirectUrl;
		} else {
			navigate({ pathname: "/listing" });
		}
	}, [signupPage.redirectUrl]);

	const heroTextPosition = signupPage?.heroTexts?.position;

	const signupClassname = classNames("signup-marketing__title", {
		"signup-marketing__title--top": heroTextPosition === "top",
		"signup-marketing__title--bottom": heroTextPosition === "bottom",
		"signup-marketing__title--center": heroTextPosition === "center",
	});

	return (
		<div className="signup-marketing" data-testid="signup-marketing">
			<section className="signup-marketing__hero">
				<div
					className={photoClass}
					data-testid={`signup-marketing-photo-${signupPage.photoSmallBackgroundHeight}`}
				>
					<SignupPageBackground
						resolution={resolution}
						backgroundPosition={signupPage.backgroundPosition}
						sourceForegroundLargeUrl={signupPage.photoLargeTextUrl}
						sourceBackgroundLargeUrl={signupPage.photoLargeBackgroundUrl}
						sourceForegroundSmallUrl={signupPage.photoSmallTextUrl}
						sourceBackgroundSmallUrl={signupPage.photoSmallBackgroundUrl}
						sourceBackgroundLargeThumbnail={signupPage.photoLargeBackgroundThumbnail}
						sourceBackgroundSmallThumbnail={signupPage.photoSmallBackgroundThumbnail}
					/>

					<div className="signup-marketing__hero-overlay">
						<div className={signupClassname}>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL5}
								variantMD={TYPOGRAPHY_VARIANTS.XL6}
								isBold
							>
								{signupPage?.heroTexts?.title}
							</Typography>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								variantMD={TYPOGRAPHY_VARIANTS.XL3}
								isBold
							>
								{heroSubtitle}
							</Typography>
						</div>

						{isDesktop && (
							<div className="signup-marketing__auth-form">
								<AuthForm
									onAuthSuccess={redirectOnAuthSuccess}
									defaultPage={defaultPage}
								/>
							</div>
						)}
					</div>
				</div>

				{!isDesktop && (
					<div className="signup-marketing__auth-form">
						<AuthForm onAuthSuccess={redirectOnAuthSuccess} defaultPage={defaultPage} />
					</div>
				)}
			</section>
		</div>
	);
};

SignupMarketing.propTypes = {
	resolution: resolutionPropType,
	signupPage: signupPageAuthShape,
};

export default memo(SignupMarketing);
